import { useState, useEffect } from 'react';
import { getPortfolioAnalysis, getInvestorProfile, getDemoTier } from '../lib/cerebro';

// Tailwind palette for portfolio composition donut
const COMPOSITION_COLORS = [
  '#0a2540', '#2A59C6', '#7C3AED', '#059669',
  '#DC2626', '#F59E0B', '#0891B2', '#DB2777',
];

/**
 * PortfolioAnalysis — Shows tiered portfolio analysis in "Mi Plan" section
 */
export default function PortfolioAnalysis() {
  const [profile, setProfile] = useState(null);
  const [analysis, setAnalysis] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    loadData();
  }, []);

  async function loadData() {
    setLoading(true);
    try {
      const prof = await getInvestorProfile();
      setProfile(prof);

      const demoTier = getDemoTier();
      const anal = await getPortfolioAnalysis(demoTier);
      setAnalysis(anal);
    } catch (err) {
      setError('Completa el onboarding para ver tu analisis de portafolio');
    } finally {
      setLoading(false);
    }
  }

  if (loading) {
    return (
      <div className="animate-pulse space-y-3 p-4">
        <div className="h-5 bg-[#F3F4F6] rounded w-1/2" />
        <div className="h-4 bg-[#F3F4F6] rounded w-full" />
        <div className="h-4 bg-[#F3F4F6] rounded w-3/4" />
      </div>
    );
  }

  if (error || !profile) {
    return (
      <div className="p-4 bg-[#F9FAFB] rounded-2xl text-center">
        <span className="text-2xl mb-2 block">🧠</span>
        <p className="text-sm text-[#6B7280]">{error || 'Completa el onboarding para activar tu analisis'}</p>
      </div>
    );
  }

  const portfolioNames = {
    guardian: 'Guardian',
    explorer: 'Explorador',
    visionary: 'Visionario',
    income_builder: 'Constructor de Ingresos',
    custom: 'Personalizado',
  };

  const portfolioIcons = {
    guardian: '🛡️',
    explorer: '🧭',
    visionary: '🚀',
    income_builder: '💰',
    custom: '🎨',
  };

  const tierLabels = {
    basic: 'Basico',
    intermediate: 'Intermedio',
    advanced: 'Avanzado',
    premium: 'Premium',
  };

  return (
    <div className="space-y-4">
      {/* Portfolio header */}
      <div className="bg-white rounded-2xl border border-[#E5E7EB] p-4">
        <div className="flex items-center gap-3 mb-3">
          <div className="w-10 h-10 rounded-xl bg-[#F0F4FF] flex items-center justify-center text-xl">
            {portfolioIcons[profile.portfolio] || '📊'}
          </div>
          <div>
            <div className="text-sm font-semibold text-[#181D27]">
              Portafolio {portfolioNames[profile.portfolio] || profile.portfolio}
            </div>
            <div className="text-xs text-[#9CA3AF]">
              Tier: {tierLabels[analysis?.userTier || profile.tier] || 'Basico'}
            </div>
          </div>
        </div>

        {/* Thesis */}
        {(analysis?.thesis || profile.thesis) && (
          <div className="bg-[#F0F4FF] rounded-xl p-3">
            <div className="text-[10px] font-semibold text-[#2A59C6] uppercase mb-1">Tu tesis</div>
            <p className="text-xs text-[#4B5563] leading-relaxed">{analysis?.thesis || profile.thesis}</p>
          </div>
        )}
      </div>

      {/* Composition card */}
      {analysis?.composition && analysis.composition.length > 0 && (
        <PortfolioComposition composition={analysis.composition} />
      )}

      {/* Analysis content */}
      {analysis?.analysis && (
        <div className="bg-white rounded-2xl border border-[#E5E7EB] p-4">
          <div className="flex items-center gap-2 mb-3">
            <span className="text-sm">🔮</span>
            <span className="text-sm font-semibold text-[#181D27]">El Oráculo</span>
          </div>
          <p className="text-sm text-[#4B5563] leading-relaxed whitespace-pre-line">{analysis.analysis}</p>
        </div>
      )}

      {/* Deep Dive 2.0 — only if generated */}
      {analysis?.deepDive?.philosophy && (
        <PortfolioDeepDive2
          data={analysis.deepDive}
          userTier={getDemoTier() || analysis?.userTier || profile.tier || 'basic'}
        />
      )}

      {/* Upgrade CTA */}
      {profile.tier === 'basic' && (
        <div className="bg-gradient-to-r from-[#F0F4FF] to-[#F5F3FF] rounded-xl p-4 border border-[#E5E7EB]">
          <div className="flex items-center gap-2 mb-2">
            <span className="text-lg">📈</span>
            <span className="text-sm font-semibold text-[#181D27]">Sube de nivel</span>
          </div>
          <p className="text-xs text-[#6B7280] leading-relaxed">
            Con mas inversion desbloqueamos: sector rotation, macro outlook, riesgo detallado del portafolio, y deep dives semanales.
          </p>
        </div>
      )}
    </div>
  );
}

// ============================================================================
// Sub-components
// ============================================================================

/**
 * PortfolioComposition — Shows the portfolio allocation as a horizontal bar
 * with one segment per ticker (color-coded), plus a legend below.
 */
function PortfolioComposition({ composition }) {
  const total = composition.reduce((s, c) => s + (c.weight || 0), 0);
  // Sort largest to smallest for nicer visual
  const sorted = [...composition].sort((a, b) => b.weight - a.weight);

  return (
    <div className="bg-white rounded-2xl border border-[#E5E7EB] p-4">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <span className="text-sm">🧩</span>
          <span className="text-sm font-semibold text-[#181D27]">Composición</span>
        </div>
        <span className="text-[10px] text-[#9CA3AF]">{sorted.length} posiciones</span>
      </div>

      {/* Bar visualization */}
      <div className="flex h-3 rounded-full overflow-hidden mb-3 bg-[#F3F4F6]">
        {sorted.map((c, i) => (
          <div
            key={c.ticker}
            style={{
              width: `${(c.weight / total) * 100}%`,
              backgroundColor: COMPOSITION_COLORS[i % COMPOSITION_COLORS.length],
            }}
            title={`${c.ticker} ${(c.weight * 100).toFixed(0)}%`}
          />
        ))}
      </div>

      {/* Legend */}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-3 gap-y-1.5">
        {sorted.map((c, i) => (
          <div key={c.ticker} className="flex items-center gap-1.5 text-xs">
            <span
              className="w-2 h-2 rounded-full flex-shrink-0"
              style={{ backgroundColor: COMPOSITION_COLORS[i % COMPOSITION_COLORS.length] }}
            />
            <span className="font-semibold text-[#181D27]">{c.ticker}</span>
            <span className="text-[#9CA3AF]">{(c.weight * 100).toFixed(0)}%</span>
          </div>
        ))}
      </div>
    </div>
  );
}

/**
 * Tier-based visibility map for portfolio deep dive sections.
 * Each section unlocks at a given tier level.
 */
const TIER_ORDER_P = ['basic', 'intermediate', 'advanced', 'premium'];
const tierIdxP = (t) => TIER_ORDER_P.indexOf(t || 'basic');

const SECTION_TIER = {
  philosophy:       'basic',         // Hero — always visible
  forWhom:          'basic',         // Para ti si
  antiFit:          'basic',         // No es para ti si
  mentalCompanion:  'intermediate',  // Coaching behavioral
  centralBets:      'advanced',
  balance:          'advanced',
  macroBehavior:    'premium',
  historicalDrawdown: 'premium',
  preMortem:        'premium',
  bearCase:         'premium',
};

const TIER_LABEL = {
  intermediate: 'Intermedio (invierte $1K+)',
  advanced:     'Avanzado (invierte $10K+)',
  premium:      'Premium (invierte $50K+)',
};

const TIER_ACCENT = {
  intermediate: '#F59E0B',
  advanced:     '#7C3AED',
  premium:      '#DC2626',
};

/**
 * PortfolioDeepDive2 — Renders the 10-lens portfolio deep dive (Cerebro 2.0).
 * Sections gated by user's tier; locked sections show CTA to unlock.
 */
function PortfolioDeepDive2({ data, userTier = 'basic' }) {
  const [expanded, setExpanded] = useState({});
  const toggle = (k) => setExpanded((p) => ({ ...p, [k]: !p[k] }));

  const userIdx = tierIdxP(userTier);
  const canSee = (sectionKey) => userIdx >= tierIdxP(SECTION_TIER[sectionKey]);

  return (
    <div className="space-y-3">
      {/* Philosophy — Hero (always visible) */}
      <div className="bg-gradient-to-br from-[#0a2540] to-[#1a3a6c] rounded-2xl p-5 text-white">
        <div className="text-[10px] uppercase tracking-wider opacity-70 mb-1.5">Filosofía del Portafolio</div>
        <p className="text-base font-medium leading-relaxed">{data.philosophy}</p>
      </div>

      {/* For Whom — Green (basic+) */}
      {data.forWhom && (
        <div className="bg-[#F0FDF4] border border-[#86EFAC] rounded-2xl p-4">
          <div className="flex items-center gap-1.5 mb-1.5">
            <span className="text-xs">✅</span>
            <span className="text-[10px] uppercase tracking-wider font-semibold text-[#059669]">Para ti si...</span>
          </div>
          <p className="text-sm text-[#14532D] leading-relaxed whitespace-pre-line">{data.forWhom}</p>
        </div>
      )}

      {/* Anti-Fit — Red (basic+) */}
      {data.antiFit && (
        <div className="bg-[#FEF2F2] border border-[#FCA5A5] rounded-2xl p-4">
          <div className="flex items-center gap-1.5 mb-1.5">
            <span className="text-xs">⚠️</span>
            <span className="text-[10px] uppercase tracking-wider font-semibold text-[#DC2626]">No es para ti si...</span>
          </div>
          <p className="text-sm text-[#7F1D1D] leading-relaxed whitespace-pre-line">{data.antiFit}</p>
        </div>
      )}

      {/* Mental Companion — Blue (intermediate+) */}
      {data.mentalCompanion && (canSee('mentalCompanion') ? (
        <div className="bg-[#EFF6FF] border border-[#93C5FD] rounded-2xl p-4">
          <div className="flex items-center gap-1.5 mb-1.5">
            <span className="text-xs">🧠</span>
            <span className="text-[10px] uppercase tracking-wider font-semibold text-[#1E40AF]">Compañero de viaje</span>
          </div>
          <p className="text-sm text-[#1E3A8A] leading-relaxed whitespace-pre-line">{data.mentalCompanion}</p>
        </div>
      ) : (
        <LockedTeaser
          title="Compañero de viaje"
          icon="🧠"
          requiredTier="intermediate"
          preview={data.mentalCompanion}
        />
      ))}

      {/* Apuestas centrales (advanced+) */}
      {data.centralBets && data.centralBets.length > 0 && (canSee('centralBets') ? (
        <PCollapsible
          title="Apuestas centrales"
          icon="🎯"
          isOpen={expanded.bets}
          onToggle={() => toggle('bets')}
        >
          <ul className="space-y-1.5">
            {data.centralBets.map((b, i) => (
              <li key={i} className="text-sm text-[#4B5563] leading-relaxed flex gap-2">
                <span className="text-[#7C3AED] font-bold flex-shrink-0">→</span>
                <span>{b}</span>
              </li>
            ))}
          </ul>
        </PCollapsible>
      ) : (
        <LockedTeaser title="Apuestas centrales" icon="🎯" requiredTier="advanced" preview={data.centralBets.join(' · ')} />
      ))}

      {/* Balance (advanced+) */}
      {data.balance && (canSee('balance') ? (
        <PCollapsible title="El balance" icon="⚖️" isOpen={expanded.balance} onToggle={() => toggle('balance')}>
          <p className="text-sm text-[#4B5563] leading-relaxed">{data.balance}</p>
        </PCollapsible>
      ) : (
        <LockedTeaser title="El balance" icon="⚖️" requiredTier="advanced" preview={data.balance} />
      ))}

      {/* Macro behavior (premium+) */}
      {data.macroBehavior && data.macroBehavior.ratesUp && (canSee('macroBehavior') ? (
        <PCollapsible
          title="Cómo se comporta en cada macro"
          icon="🌎"
          isOpen={expanded.macro}
          onToggle={() => toggle('macro')}
        >
          <div className="space-y-2.5">
            <MacroRow label="Si tasas suben"  text={data.macroBehavior.ratesUp} />
            <MacroRow label="Si tasas bajan"  text={data.macroBehavior.ratesDown} />
            <MacroRow label="Con inflación"   text={data.macroBehavior.inflation} />
            <MacroRow label="En recesión"     text={data.macroBehavior.recession} />
            <MacroRow label="Con boom tech"   text={data.macroBehavior.techBoom} />
          </div>
        </PCollapsible>
      ) : (
        <LockedTeaser title="Cómo se comporta en cada macro" icon="🌎" requiredTier="premium" preview={data.macroBehavior.ratesUp} />
      ))}

      {/* Historical drawdown (premium+) */}
      {data.historicalDrawdown && (canSee('historicalDrawdown') ? (
        <PCollapsible
          title="Stress test histórico"
          icon="🎢"
          isOpen={expanded.drawdown}
          onToggle={() => toggle('drawdown')}
        >
          <p className="text-sm text-[#4B5563] leading-relaxed">{data.historicalDrawdown}</p>
        </PCollapsible>
      ) : (
        <LockedTeaser title="Stress test histórico" icon="🎢" requiredTier="premium" preview={data.historicalDrawdown} />
      ))}

      {/* Pre-mortem (premium+) */}
      {data.preMortem && (canSee('preMortem') ? (
        <PCollapsible
          title="Pre-Mortem — Es 2031, salió bien"
          icon="🔮"
          isOpen={expanded.premortem}
          onToggle={() => toggle('premortem')}
        >
          <p className="text-sm text-[#4B5563] leading-relaxed">{data.preMortem}</p>
        </PCollapsible>
      ) : (
        <LockedTeaser title="Pre-Mortem 2031" icon="🔮" requiredTier="premium" preview={data.preMortem} />
      ))}

      {/* Bear case (premium+) */}
      {data.bearCase && (canSee('bearCase') ? (
        <PCollapsible
          title="Bear case del portafolio"
          icon="🐻"
          isOpen={expanded.bear}
          onToggle={() => toggle('bear')}
        >
          <p className="text-sm text-[#4B5563] leading-relaxed">{data.bearCase}</p>
        </PCollapsible>
      ) : (
        <LockedTeaser title="Bear case" icon="🐻" requiredTier="premium" preview={data.bearCase} />
      ))}

      {/* Footer */}
      {data.generatedAt && (
        <div className="text-[10px] text-[#9CA3AF] text-right">
          Análisis generado el {new Date(data.generatedAt).toLocaleDateString('es-CO')}
          {data.modelUsed && ` · ${data.modelUsed.replace('claude-', '')}`}
        </div>
      )}
    </div>
  );
}

/**
 * LockedTeaser — Shown when the section is gated above the user's tier.
 * Renders the title + a blurred preview + an unlock CTA.
 */
function LockedTeaser({ title, icon, requiredTier, preview }) {
  const accent = TIER_ACCENT[requiredTier] || '#7C3AED';
  return (
    <div className="bg-white border border-[#E5E7EB] rounded-2xl p-4 relative overflow-hidden">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <span className="text-sm opacity-50">{icon}</span>
          <span className="text-sm font-medium text-[#9CA3AF]">{title}</span>
        </div>
        <span className="text-[10px] font-semibold uppercase tracking-wider" style={{ color: accent }}>
          🔒 Bloqueado
        </span>
      </div>

      {preview && (
        <div className="relative max-h-16 overflow-hidden mb-3">
          <p className="text-xs text-[#9CA3AF] leading-relaxed blur-sm select-none">
            {String(preview).substring(0, 220)}…
          </p>
          <div className="absolute inset-0 bg-gradient-to-b from-transparent to-white pointer-events-none" />
        </div>
      )}

      <div
        className="inline-flex items-center gap-1.5 text-[11px] font-semibold px-2.5 py-1 rounded-full"
        style={{ backgroundColor: accent + '15', color: accent }}
      >
        <span>Desbloquea</span>
        <span>·</span>
        <span>{TIER_LABEL[requiredTier]}</span>
      </div>
    </div>
  );
}

function PCollapsible({ title, icon, isOpen, onToggle, children }) {
  return (
    <div className="bg-white border border-[#E5E7EB] rounded-2xl overflow-hidden">
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between px-4 py-3 hover:bg-[#F9FAFB] transition"
      >
        <div className="flex items-center gap-2">
          <span className="text-sm">{icon}</span>
          <span className="text-sm font-medium text-[#181D27]">{title}</span>
        </div>
        <svg
          width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#9CA3AF"
          strokeWidth="2" strokeLinecap="round"
          className={`transition-transform ${isOpen ? 'rotate-180' : ''}`}
        >
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </button>
      {isOpen && <div className="px-4 pb-4">{children}</div>}
    </div>
  );
}

function MacroRow({ label, text }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider font-semibold text-[#7C3AED] mb-0.5">{label}</div>
      <p className="text-xs text-[#4B5563] leading-relaxed">{text}</p>
    </div>
  );
}
